import { useState } from 'react'
import { useEstimate } from '../context/EstimateContext'
import { TIME_WINDOWS } from '../config/constants'
import { priceRange, formatPhoneInput, isValidPhone, isValidEmail, normalizePhone } from '../lib/format'
import Spinner from '../components/Spinner'
import ErrorNotice from '../components/ErrorNotice'
import Disclaimer from '../components/Disclaimer'

export default function BookingStep() {
  const { estimate, chosenOptionType, submitBooking, busy, error } = useEstimate()

  const chosen =
    estimate?.options?.find((o) => o.type === chosenOptionType) || estimate?.options?.[0] || null

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    timeWindow: '',
    notes: '',
  })
  const [touched, setTouched] = useState(false)

  const errors = validate(form)
  const isValid = Object.keys(errors).length === 0

  function update(key, value) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  function handleSubmit(e) {
    e.preventDefault()
    setTouched(true)
    if (!isValid || busy) return
    submitBooking({ ...form, phone: normalizePhone(form.phone) })
  }

  return (
    <form onSubmit={handleSubmit} className="animate-fade-in" noValidate>
      <h1 className="text-[26px] font-bold leading-tight text-ink">Book your appointment</h1>
      <p className="mt-2.5 text-sm leading-relaxed text-muted">
        No payment now — you pay on-site only after the work is done and you&apos;re happy.
      </p>

      {chosen && (
        <div className="mt-5 flex items-center justify-between gap-3 rounded-3xl bg-brand-grad p-[1.5px] shadow-glass">
          <div className="flex w-full items-center justify-between rounded-[22px] bg-white/90 px-4 py-3.5 backdrop-blur">
            <div>
              <p className="eyebrow capitalize text-brand-600">{chosen.type}</p>
              <p className="text-sm font-medium text-subink">{chosen.label}</p>
            </div>
            <p className="font-display text-lg font-bold tabular-nums text-ink">
              {priceRange(chosen.totalLow, chosen.totalHigh)}
            </p>
          </div>
        </div>
      )}

      <ErrorNotice message={error} />

      <div className="mt-5 space-y-4">
        <Field label="Full name" error={touched && errors.name}>
          <input
            className="input"
            type="text"
            autoComplete="name"
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder="Jane Smith"
          />
        </Field>

        <Field label="Mobile phone" hint="Your technician will call or text to confirm." error={touched && errors.phone}>
          <input
            className="input"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={form.phone}
            onChange={(e) => update('phone', formatPhoneInput(e.target.value))}
            placeholder="(561) 555-1234"
          />
        </Field>

        <Field label="Service address" error={touched && errors.address}>
          <input
            className="input"
            type="text"
            autoComplete="street-address"
            value={form.address}
            onChange={(e) => update('address', e.target.value)}
            placeholder="123 Palmetto Dr, Boca Raton, FL"
          />
        </Field>

        <Field label="Email" hint="Optional — for your receipt." error={touched && errors.email}>
          <input
            className="input"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
            placeholder="you@email.com"
          />
        </Field>

        <Field label="Preferred time" error={touched && errors.timeWindow}>
          <div className="grid grid-cols-2 gap-2.5">
            {TIME_WINDOWS.map((w) => {
              const active = form.timeWindow === w.key
              return (
                <button
                  key={w.key}
                  type="button"
                  onClick={() => update('timeWindow', w.key)}
                  className={`rounded-2xl border p-3.5 text-left transition ${
                    active
                      ? 'border-brand-400 bg-brand-50 ring-2 ring-brand-100'
                      : 'border-hairline bg-white/70 backdrop-blur hover:bg-white'
                  }`}
                >
                  <span className={`block text-sm font-semibold ${active ? 'text-brand-800' : 'text-ink'}`}>
                    {w.label}
                  </span>
                  <span className="block text-xs text-muted">{w.detail}</span>
                </button>
              )
            })}
          </div>
        </Field>

        <Field label="Anything we should know?" hint="Optional — gate code, pets, access notes.">
          <textarea
            className="input min-h-[84px] resize-y"
            value={form.notes}
            onChange={(e) => update('notes', e.target.value)}
            placeholder="Water heater is in the garage, gate code 1234…"
          />
        </Field>
      </div>

      <button type="submit" className="btn-primary mt-7 flex items-center justify-center gap-2" disabled={busy}>
        {busy ? (
          <>
            <Spinner className="h-5 w-5 text-white" />
            Booking…
          </>
        ) : (
          'Confirm booking'
        )}
      </button>

      <Disclaimer />
    </form>
  )
}

function validate(form) {
  const errors = {}
  if (!form.name.trim()) errors.name = 'Please enter your name.'
  if (!isValidPhone(form.phone)) errors.phone = 'Enter a 10-digit mobile number.'
  if (!form.address.trim()) errors.address = 'Please enter your service address.'
  if (!isValidEmail(form.email)) errors.email = 'That email doesn’t look right.'
  if (!form.timeWindow) errors.timeWindow = 'Pick a preferred time.'
  return errors
}

function Field({ label, hint, error, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-subink">{label}</span>
      {children}
      {error ? (
        <span className="mt-1 block text-xs text-red-600">{error}</span>
      ) : (
        hint && <span className="mt-1 block text-xs text-muted">{hint}</span>
      )}
    </label>
  )
}
